import React, { useState, useEffect, useMemo } from 'react';
import { fetchLegalClauses, createLegalClause, updateLegalClause, deleteLegalClause } from '../api';

const CATEGORY_OPTIONS = [
  'Standard', 'Confidentiality', 'Indemnity', 'Limitation', 'Termination',
  'Non-Compete', 'Force Majeure', 'Dispute Resolution', 'Governing Law',
  'Warranty', 'Payment Terms', 'Other',
];

const ClauseLibrary = ({ authAxios, showFormMessage, userRole }) => {
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'Standard', description: '' });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const canEdit = ['PARTNER', 'SUPERUSER', 'ASSOCIATE'].includes(userRole);

  const loadClauses = async () => {
    setLoading(true);
    try {
      const data = await fetchLegalClauses(authAxios);
      setClauses(data.results || data);
    } catch {
      showFormMessage('error', 'Failed to load clauses');
    }
    setLoading(false);
  };

  useEffect(() => { loadClauses(); }, [authAxios]);

  const filtered = useMemo(() => {
    return clauses.filter(c => {
      const matchesSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase());
      const matchesCat = !filterCategory || c.category === filterCategory;
      return matchesSearch && matchesCat;
    });
  }, [clauses, search, filterCategory]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      showFormMessage('error', 'Title and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await updateLegalClause(authAxios, editId, form);
        showFormMessage('success', 'Clause updated');
      } else {
        await createLegalClause(authAxios, form);
        showFormMessage('success', 'Clause created');
      }
      setShowForm(false);
      setEditId(null);
      setForm({ title: '', content: '', category: 'Standard', description: '' });
      await loadClauses();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save clause');
    }
    setSaving(false);
  };

  const handleEdit = (clause) => {
    setEditId(clause.id);
    setForm({ title: clause.title, content: clause.content, category: clause.category, description: clause.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this clause?')) return;
    try {
      await deleteLegalClause(authAxios, id);
      showFormMessage('success', 'Clause deleted');
      await loadClauses();
    } catch {
      showFormMessage('error', 'Failed to delete clause');
    }
  };

  const handleCopy = (clause) => {
    navigator.clipboard.writeText(clause.content);
    showFormMessage('success', 'Clause content copied to clipboard');
  };

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(c => {
      if (!groups[c.category]) groups[c.category] = [];
      groups[c.category].push(c);
    });
    return groups;
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Legal Clause Library</h2>
          <p className="text-sm text-gray-500">Reusable clauses for documents and templates</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <input
            type="text"
            placeholder="Search clauses..."
            className="border rounded px-3 py-2 text-sm w-56"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="border rounded px-3 py-2 text-sm"
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {canEdit && (
            <button
              onClick={() => { setShowForm(true); setEditId(null); setForm({ title: '', content: '', category: 'Standard', description: '' }); }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm whitespace-nowrap"
            >
              + New Clause
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-lg mb-4">{editId ? 'Edit' : 'New'} Clause</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" className="border rounded p-2 w-full" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select className="border rounded p-2 w-full" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input type="text" className="border rounded p-2 w-full" placeholder="Brief description of when to use this clause" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
              <textarea className="border rounded p-2 w-full h-40 font-mono text-sm" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Enter the clause text..." />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm">Cancel</button>
              <button disabled={saving} onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50">
                {saving ? 'Saving...' : editId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clause List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {clauses.length === 0 ? 'No clauses yet. Create your first clause to get started.' : 'No clauses match your search.'}
        </div>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
          <div key={category} className="bg-white rounded-lg shadow">
            <div className="px-6 py-3 bg-gray-50 border-b rounded-t-lg">
              <h3 className="font-semibold text-gray-700">{category} <span className="text-sm text-gray-400">({items.length})</span></h3>
            </div>
            <div className="divide-y">
              {items.map(clause => (
                <div key={clause.id} className="px-6 py-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 cursor-pointer" onClick={() => setExpandedId(expandedId === clause.id ? null : clause.id)}>
                      <div className="font-medium text-gray-800">{clause.title}</div>
                      {clause.description && <div className="text-sm text-gray-500 mt-0.5">{clause.description}</div>}
                      <div className="text-xs text-gray-400 mt-1">
                        by {clause.created_by_username || 'System'} &middot; {new Date(clause.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-3">
                      <button onClick={() => handleCopy(clause)} className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" title="Copy to clipboard">
                        Copy
                      </button>
                      {canEdit && (
                        <>
                          <button onClick={() => handleEdit(clause)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Edit</button>
                          <button onClick={() => handleDelete(clause.id)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                  {expandedId === clause.id && (
                    <div className="mt-3 p-3 bg-gray-50 rounded border text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: clause.content }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ClauseLibrary;
